import { useEffect, useState, useCallback, useMemo } from "react";
import { BufferGeometry, BufferAttribute, Mesh, Vector3 } from "three";
import { MeshSurfaceSampler } from "three/examples/jsm/math/MeshSurfaceSampler.js";
import {
  PointCloudGeneratorResult,
  PointCloudConfig,
  LaserPhysicsConfig,
} from "@/types/laser-engraving";
import {
  LASER_PHYSICS_CONSTANTS,
  LAYER_SCALING,
  DEFAULT_POINT_CLOUD_CONFIG,
} from "@/lib/laser-physics";

/**
 * 点云生成Hook
 *
 * 负责从源几何体生成激光内雕点云，包括：
 * - 分层体积采样算法
 * - 激光物理光学模型计算
 * - 深度衰减和颜色配比
 * - 性能优化和错误处理
 *
 * @param sourceGeometry - 源几何体
 * @param pointCloudConfig - 点云配置参数
 * @param laserPhysicsConfig - 激光物理参数配置
 * @param shouldGenerate - 是否应该生成点云
 * @returns 点云生成结果
 */
export function usePointCloudGenerator(
  sourceGeometry: BufferGeometry | null,
  pointCloudConfig: PointCloudConfig = DEFAULT_POINT_CLOUD_CONFIG,
  laserPhysicsConfig: LaserPhysicsConfig = LASER_PHYSICS_CONSTANTS,
  shouldGenerate: boolean = true,
): PointCloudGeneratorResult {
  // 状态管理
  const [pointGeometry, setPointGeometry] = useState<BufferGeometry | null>(
    null,
  );
  const [isGenerating, setIsGenerating] = useState(false);
  const [isFullyInitialized, setIsFullyInitialized] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [generatedPointCount, setGeneratedPointCount] = useState(0);

  // 重置错误状态
  const resetError = useCallback(() => {
    setError(null);
  }, []);

  // 清理点云几何体
  const clearPointGeometry = useCallback(() => {
    if (pointGeometry) {
      pointGeometry.dispose();
      setPointGeometry(null);
      setGeneratedPointCount(0);
      setIsFullyInitialized(false);
    }
  }, [pointGeometry]);

  // 计算分层采样参数
  const layerParameters = useMemo(() => {
    const { pointCount } = pointCloudConfig;
    const { layers } = laserPhysicsConfig;
    const pointsPerLayer = Math.floor(pointCount / layers);

    return {
      layers,
      pointsPerLayer,
      totalPoints: pointsPerLayer * layers,
    };
  }, [pointCloudConfig.pointCount, laserPhysicsConfig.layers]);

  // 🎯 核心点云生成逻辑
  useEffect(() => {
    if (!shouldGenerate || !sourceGeometry) {
      setPointGeometry(null);
      setGeneratedPointCount(0);
      setIsFullyInitialized(false);
      return;
    }

    let isActive = true; // 用于防止竞态条件

    const generatePointCloud = async () => {
      try {
        setIsGenerating(true);
        setError(null);

        console.log("🎯 开始生成点云（usePointCloudGenerator）...");

        // 创建点云几何体
        const pointsGeometry = new BufferGeometry();
        const tempMesh = new Mesh(sourceGeometry);
        const baseSampler = new MeshSurfaceSampler(tempMesh).build();

        const positions: number[] = [];
        const colors: number[] = [];
        const tempPosition = new Vector3();

        // 分层采样 - 实现体积填充效果
        for (let layer = 0; layer < layerParameters.layers; layer++) {
          if (!isActive) break; // 检查是否已取消

          // 计算当前层的缩放比例
          const layerScale =
            LAYER_SCALING.BASE_SCALE -
            layer * LAYER_SCALING.SCALE_REDUCTION_PER_LAYER;
          tempMesh.scale.setScalar(layerScale);
          tempMesh.updateMatrixWorld();

          // 为当前层创建新的采样器
          const layerSampler = new MeshSurfaceSampler(tempMesh).build();

          // 在当前层采样点
          for (let i = 0; i < layerParameters.pointsPerLayer; i++) {
            if (!isActive) break; // 检查是否已取消

            // 采样位置
            layerSampler.sample(tempPosition);
            positions.push(tempPosition.x, tempPosition.y, tempPosition.z);

            // 🎯 激光内雕物理光学模型 - 基于真实激光烧蚀工艺
            const {
              laserAblationReflectance,
              depthAttenuationFactor,
              brightnessGradient,
              spectrumRGB,
            } = laserPhysicsConfig;

            // 深度衰减计算 - 基于比尔-朗伯定律
            const layerAttenuation = Math.pow(depthAttenuationFactor, layer);
            const baseIntensity = laserAblationReflectance * layerAttenuation;

            // 表层激光点亮度梯度增强
            const brightness = 1.0 + layer * brightnessGradient;

            // 激光能量光谱的物理准确RGB配比
            const enhancedRGB = {
              red: spectrumRGB.red * brightness,
              green: spectrumRGB.green * brightness,
              blue: spectrumRGB.blue * brightness,
            };

            // 计算最终颜色
            colors.push(
              baseIntensity * enhancedRGB.red,
              baseIntensity * enhancedRGB.green,
              baseIntensity * enhancedRGB.blue,
            );
          }

          // 添加小延迟，防止阻塞UI线程
          if (layer % 2 === 0) {
            await new Promise((resolve) => setTimeout(resolve, 0));
          }
        }

        if (!isActive) return; // 如果已取消，不设置结果

        // 设置点云几何体属性
        pointsGeometry.setAttribute(
          "position",
          new BufferAttribute(new Float32Array(positions), 3),
        );
        pointsGeometry.setAttribute(
          "color",
          new BufferAttribute(new Float32Array(colors), 3),
        );

        const finalPointCount = positions.length / 3;
        console.log(`🎯 点云生成完成: 总点数=${finalPointCount}`);

        // 🎯 先更新几何体，但还未完全初始化
        setPointGeometry(pointsGeometry);
        setGeneratedPointCount(finalPointCount);
        setIsGenerating(false);

        // 🎯 确保几何体属性完全同步到GPU - 延迟2帧确认
        await new Promise((resolve) => {
          let frameCount = 0;
          const checkInitialization = () => {
            frameCount++;

            // 验证几何体属性完整性
            const positionAttr = pointsGeometry.getAttribute("position");
            const colorAttr = pointsGeometry.getAttribute("color");

            const isGeometryValid =
              positionAttr &&
              colorAttr &&
              positionAttr.count > 0 &&
              colorAttr.count > 0 &&
              positionAttr.count === colorAttr.count;

            if (frameCount >= 2 && isGeometryValid) {
              console.log("🎯 点云几何体完全初始化验证通过");
              if (isActive) {
                setIsFullyInitialized(true);
              }
              resolve(undefined);
            } else if (frameCount < 10) {
              // 最多等待10帧
              requestAnimationFrame(checkInitialization);
            } else {
              console.warn("🎯 点云几何体初始化验证超时");
              if (isActive) {
                setIsFullyInitialized(true); // 超时后也认为初始化完成
              }
              resolve(undefined);
            }
          };

          requestAnimationFrame(checkInitialization);
        });
      } catch (err) {
        if (!isActive) return; // 如果已取消，不设置错误

        const errorMessage =
          err instanceof Error ? err.message : "点云生成失败";
        console.error("点云生成错误:", errorMessage);
        setError(errorMessage);
        setIsGenerating(false);
      }
    };

    // 启动异步生成
    generatePointCloud();

    // 清理函数
    return () => {
      isActive = false;
    };
  }, [
    shouldGenerate,
    sourceGeometry,
    pointCloudConfig.pointCount,
    layerParameters.layers,
    layerParameters.pointsPerLayer,
    laserPhysicsConfig.laserAblationReflectance,
    laserPhysicsConfig.depthAttenuationFactor,
    laserPhysicsConfig.brightnessGradient,
    laserPhysicsConfig.spectrumRGB.red,
    laserPhysicsConfig.spectrumRGB.green,
    laserPhysicsConfig.spectrumRGB.blue,
  ]);

  // 组件卸载时清理资源
  useEffect(() => {
    return () => {
      clearPointGeometry();
    };
  }, [clearPointGeometry]);

  return {
    pointGeometry,
    isGenerating,
    isFullyInitialized,
    error,
    generatedPointCount,
  };
}

// 类型导出，方便其他文件使用
export type { PointCloudGeneratorResult, PointCloudConfig, LaserPhysicsConfig };
